import AdvancedCheckbox from '@portal/components/AdvancedCheckbox/AdvancedCheckbox';
import AdvancedForm from '@portal/components/AdvancedForm/AdvancedForm';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import PasswordCheck from '@portal/components/PasswordCheck/PasswordCheck';
import { Col } from 'antd';
import React, { useState } from 'react';

import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { translate } from '@portal/services/i18n';

import { onboarding, passwordRules } from '@portal/config/app.json';
import { validatePassword } from '@portal/utils/validate';
import ThemedText from '@portal/components/ThemedText/ThemedText';

interface IProps {
  userData: Pick<models.OnboardingRequest, 'login' | 'password'>;
  onFormChange: (key: string, value: string) => void;
  noLogin: boolean;
  selectedBullets: string[];
  setSelectedBullets: React.Dispatch<React.SetStateAction<string[]>>;
}

const CreateAccountCredential = ({
  userData,
  onFormChange,
  noLogin,
  selectedBullets,
  setSelectedBullets,
}: IProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const getCheckName = (check: models.OnboardingCheck) =>
    typeof check === 'string' ? check : check.name;

  const handleSelectBullet = (selected: boolean, check: string) => {
    if (selected) {
      setSelectedBullets((prev: string[]) => prev.concat(check));
    } else {
      setSelectedBullets((prev) =>
        prev.filter((selectedBullet) => selectedBullet !== check)
      );
    }
  };

  return (
    <div className="create-account-credential">
      <AdvancedForm>
        {!noLogin && (
          <Col className="create-account-credential__col">
            <AdvancedInput
              label={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.CREDENTIALS.FORM.USER_LOGIN'
              )}
              value={userData.login}
              variant="standard"
              onChange={(value) =>
                onFormChange('login', value.replace(/ /g, ''))
              }
            />
            <ThemedText className="create-account-credential__text">
              {translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.CREDENTIALS.FORM.USER_LOGIN_FOOTER'
              )}
            </ThemedText>
          </Col>
        )}
        <Col className="create-account-credential__col">
          <AdvancedInput
            label={translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.CREDENTIALS.FORM.ACCESS_PASSWORD'
            )}
            value={userData.password}
            variant="outlined"
            onChange={(value) => onFormChange('password', value)}
            placeholder={'*'.repeat(passwordRules.minLength ?? 8)}
            securedTextField={!showPassword}
            endIcon={
              !showPassword ? (
                <EyeOutlined onClick={() => setShowPassword(true)} />
              ) : (
                <EyeInvisibleOutlined onClick={() => setShowPassword(false)} />
              )
            }
            maxLength={passwordRules.maxLength}
          />
          <ThemedText
            variant="body"
            className="create-account-credential__text"
          >
            {translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.CREDENTIALS.FORM.ACCESS_PASSWORD_FOOTER'
            )}
          </ThemedText>
          <PasswordCheck
            password={userData.password ?? ''}
            passwordValidation={validatePassword(userData.password ?? '')}
          />
          {onboarding.actions.checks && onboarding.actions.checks.length > 0 ? (
            <div className="create-account-credential__text">
              {onboarding.actions.checks.map(
                (check: models.OnboardingCheck) => (
                  <>
                    <AdvancedCheckbox
                      key={getCheckName(check)}
                      label={translate(
                        // eslint-disable-next-line prettier/prettier
                        `PAGES.AUTH.CREATE_NEW_PASSWORD.VALIDATION.BULLETS.${getCheckName(
                          check
                        )}`
                      )}
                      value={selectedBullets.includes(getCheckName(check))}
                      onChange={(selected) =>
                        handleSelectBullet(selected, getCheckName(check))
                      }
                    />
                    {typeof check !== 'string' &&
                    (check['mandatory'] ?? false) ? (
                      <ThemedText className="create-account-credential__text__mandatory">
                        {translate('PAGES.AUTH.CREATE_NEW_PASSWORD.MANDATORY')}
                      </ThemedText>
                    ) : null}
                  </>
                )
              )}
            </div>
          ) : null}
        </Col>
      </AdvancedForm>
    </div>
  );
};

export default CreateAccountCredential;
